import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface CalculatorInputs {
  deliveryType: 'normal' | 'c-section';
  roomType: 'general' | 'semi-private' | 'private' | 'deluxe';
  hospitalCategory: 'affordable' | 'mid-range' | 'premium';
  nicuRequired: boolean;
  daysStay: number;
  diagnosticTests: boolean;
  anesthesia: boolean;
  complications: boolean;
}

interface CostBreakdown {
  deliveryBase: number;
  roomCharges: number;
  nicuCharges: number;
  diagnosticCharges: number;
  anesthesiaCharges: number;
  complicationCharges: number;
  total: number;
}

export default function CostCalculator() {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    deliveryType: 'normal',
    roomType: 'general',
    hospitalCategory: 'mid-range',
    nicuRequired: false,
    daysStay: 2,
    diagnosticTests: true,
    anesthesia: false,
    complications: false
  });

  const [costs, setCosts] = useState<CostBreakdown>({
    deliveryBase: 0,
    roomCharges: 0,
    nicuCharges: 0,
    diagnosticCharges: 0,
    anesthesiaCharges: 0,
    complicationCharges: 0,
    total: 0
  });

  const [showBreakdown, setShowBreakdown] = useState(false);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: calculatorRef, isVisible: calculatorVisible } = useScrollAnimation();
  const { ref: comparisonRef, isVisible: comparisonVisible } = useScrollAnimation();

  // Base delivery costs by hospital category and type
  const baseCosts = {
    affordable: {
      normal: 15000,
      'c-section': 35000
    },
    'mid-range': {
      normal: 40000,
      'c-section': 80000
    },
    premium: {
      normal: 70000,
      'c-section': 150000
    }
  };

  // Room charges per day
  const roomCosts = {
    affordable: {
      general: 1000,
      'semi-private': 2000,
      private: 3500,
      deluxe: 5000
    },
    'mid-range': {
      general: 2000,
      'semi-private': 3500,
      private: 5500,
      deluxe: 8000
    },
    premium: {
      general: 3500,
      'semi-private': 6000,
      private: 10000,
      deluxe: 15000
    }
  };

  // Additional costs
  const additionalCosts = {
    affordable: {
      nicu: 5000,
      diagnostics: 2000,
      anesthesia: 3000,
      complications: 10000
    },
    'mid-range': {
      nicu: 10000,
      diagnostics: 3500,
      anesthesia: 5000,
      complications: 20000
    },
    premium: {
      nicu: 20000,
      diagnostics: 5000,
      anesthesia: 8000,
      complications: 35000
    }
  };

  useEffect(() => {
    calculateCosts();
  }, [inputs]);

  const calculateCosts = () => {
    const deliveryBase = baseCosts[inputs.hospitalCategory][inputs.deliveryType];
    const roomCharges = roomCosts[inputs.hospitalCategory][inputs.roomType] * inputs.daysStay;
    const nicuCharges = inputs.nicuRequired ? additionalCosts[inputs.hospitalCategory].nicu : 0;
    const diagnosticCharges = inputs.diagnosticTests ? additionalCosts[inputs.hospitalCategory].diagnostics : 0;
    const anesthesiaCharges = inputs.anesthesia ? additionalCosts[inputs.hospitalCategory].anesthesia : 0;
    const complicationCharges = inputs.complications ? additionalCosts[inputs.hospitalCategory].complications : 0;

    const total = deliveryBase + roomCharges + nicuCharges + diagnosticCharges + anesthesiaCharges + complicationCharges;

    setCosts({
      deliveryBase,
      roomCharges,
      nicuCharges,
      diagnosticCharges,
      anesthesiaCharges,
      complicationCharges,
      total
    });
  };

  const handleInputChange = (field: keyof CalculatorInputs, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'affordable':
        return 'from-emerald-500 to-teal-500';
      case 'mid-range':
        return 'from-amber-500 to-orange-500';
      case 'premium':
        return 'from-purple-500 to-pink-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const scrollToHospitals = () => {
    document.getElementById('hospitals')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="cost-calculator" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-blue-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-8 sm:mb-10 md:mb-12 ${headerVisible ? 'animate-fade-in-down' : 'opacity-0'}`}
        >
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 rounded-full mb-3 sm:mb-4">
            <i className="ri-calculator-line text-purple-600 text-sm sm:text-base"></i>
            <span className="text-xs sm:text-sm font-semibold text-purple-700">Cost Estimator</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">Delivery Cost Calculator</h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Get an instant estimate of your delivery costs based on your preferences and requirements
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Calculator Form */}
          <div
            ref={calculatorRef}
            className={`bg-white rounded-2xl sm:rounded-3xl shadow-xl p-5 sm:p-6 md:p-8 ${calculatorVisible ? 'animate-fade-in-left' : 'opacity-0'}`}
          >
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Your Preferences</h3>

            {/* Hospital Category */}
            <div className="mb-5 sm:mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                <i className="ri-hospital-line text-purple-600 mr-2"></i>
                Hospital Category
              </label>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {(['affordable', 'mid-range', 'premium'] as const).map((category) => (
                  <button
                    key={category}
                    onClick={() => handleInputChange('hospitalCategory', category)}
                    className={`px-2 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                      inputs.hospitalCategory === category
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category === 'mid-range' ? 'Mid-Range' : category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Delivery Type */}
            <div className="mb-5 sm:mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                <i className="ri-heart-pulse-line text-purple-600 mr-2"></i>
                Delivery Type
              </label>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {(['normal', 'c-section'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => handleInputChange('deliveryType', type)}
                    className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                      inputs.deliveryType === type
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type === 'c-section' ? 'C-Section' : 'Normal Delivery'}
                  </button>
                ))}
              </div>
            </div>

            {/* Room Type */}
            <div className="mb-5 sm:mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                <i className="ri-hotel-bed-line text-purple-600 mr-2"></i>
                Room Type
              </label>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {(['general', 'semi-private', 'private', 'deluxe'] as const).map((room) => (
                  <button
                    key={room}
                    onClick={() => handleInputChange('roomType', room)}
                    className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                      inputs.roomType === room
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {room === 'semi-private' ? 'Semi-Private' : room.charAt(0).toUpperCase() + room.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Days of Stay */}
            <div className="mb-5 sm:mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                <i className="ri-calendar-line text-purple-600 mr-2"></i>
                Days of Stay: <span className="text-purple-600">{inputs.daysStay} days</span>
              </label>
              <input
                type="range"
                min="1"
                max="7"
                value={inputs.daysStay}
                onChange={(e) => handleInputChange('daysStay', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1 day</span>
                <span>7 days</span>
              </div>
            </div>

            {/* Additional Services */}
            <div className="space-y-2 sm:space-y-3">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                <i className="ri-add-circle-line text-purple-600 mr-2"></i>
                Additional Services
              </label>

              <label className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-nurse-line text-purple-600 text-lg sm:text-xl"></i>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">NICU Required</span>
                </div>
                <input
                  type="checkbox"
                  checked={inputs.nicuRequired}
                  onChange={(e) => handleInputChange('nicuRequired', e.target.checked)}
                  className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 rounded focus:ring-purple-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-test-tube-line text-purple-600 text-lg sm:text-xl"></i>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">Diagnostic Tests</span>
                </div>
                <input
                  type="checkbox"
                  checked={inputs.diagnosticTests}
                  onChange={(e) => handleInputChange('diagnosticTests', e.target.checked)}
                  className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 rounded focus:ring-purple-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-syringe-line text-purple-600 text-lg sm:text-xl"></i>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">Anesthesia</span>
                </div>
                <input
                  type="checkbox"
                  checked={inputs.anesthesia}
                  onChange={(e) => handleInputChange('anesthesia', e.target.checked)}
                  className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 rounded focus:ring-purple-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-alert-line text-purple-600 text-lg sm:text-xl"></i>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">Complications Coverage</span>
                </div>
                <input
                  type="checkbox"
                  checked={inputs.complications}
                  onChange={(e) => handleInputChange('complications', e.target.checked)}
                  className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 rounded focus:ring-purple-500"
                />
              </label>
            </div>
          </div>

          {/* Cost Summary */}
          <div className="space-y-4 sm:space-y-6">
            {/* Total Cost Card */}
            <div
              className={`bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8 ${calculatorVisible ? 'animate-fade-in-right animation-delay-200' : 'opacity-0'}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-money-rupee-circle-line text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-medium opacity-90">Estimated Total Cost</p>
                  <p className="text-xs opacity-75">{inputs.hospitalCategory.charAt(0).toUpperCase() + inputs.hospitalCategory.slice(1)} Hospital</p>
                </div>
              </div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">₹{costs.total.toLocaleString()}</div>
              <p className="text-xs sm:text-sm opacity-90">This is an approximate estimate</p>
              
              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="mt-4 sm:mt-6 w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl font-semibold text-xs sm:text-sm hover:bg-white/30 transition-colors whitespace-nowrap"
              >
                <i className={`ri-${showBreakdown ? 'eye-off' : 'eye'}-line mr-2`}></i>
                {showBreakdown ? 'Hide' : 'View'} Cost Breakdown
              </button>
            </div>

            {/* Cost Breakdown */}
            {showBreakdown && (
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-6 md:p-8">
                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Cost Breakdown</h4>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <i className="ri-heart-pulse-line text-purple-600 text-sm sm:text-base"></i>
                      <span className="text-xs sm:text-sm text-gray-700">Delivery Base Cost</span>
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-gray-900">₹{costs.deliveryBase.toLocaleString()}</span>
                  </div>

                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <i className="ri-hotel-bed-line text-purple-600 text-sm sm:text-base"></i>
                      <span className="text-xs sm:text-sm text-gray-700">Room Charges ({inputs.daysStay} days)</span>
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-gray-900">₹{costs.roomCharges.toLocaleString()}</span>
                  </div>

                  {inputs.nicuRequired && (
                    <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <i className="ri-nurse-line text-purple-600 text-sm sm:text-base"></i>
                        <span className="text-xs sm:text-sm text-gray-700">NICU Charges</span>
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-gray-900">₹{costs.nicuCharges.toLocaleString()}</span>
                    </div>
                  )}

                  {inputs.diagnosticTests && (
                    <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <i className="ri-test-tube-line text-purple-600 text-sm sm:text-base"></i>
                        <span className="text-xs sm:text-sm text-gray-700">Diagnostic Tests</span>
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-gray-900">₹{costs.diagnosticCharges.toLocaleString()}</span>
                    </div>
                  )}

                  {inputs.anesthesia && (
                    <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <i className="ri-syringe-line text-purple-600 text-sm sm:text-base"></i>
                        <span className="text-xs sm:text-sm text-gray-700">Anesthesia</span>
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-gray-900">₹{costs.anesthesiaCharges.toLocaleString()}</span>
                    </div>
                  )}

                  {inputs.complications && (
                    <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <i className="ri-alert-line text-purple-600 text-sm sm:text-base"></i>
                        <span className="text-xs sm:text-sm text-gray-700">Complications Coverage</span>
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-gray-900">₹{costs.complicationCharges.toLocaleString()}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t-2 border-purple-200">
                    <span className="text-sm sm:text-base font-bold text-gray-900">Total Estimate</span>
                    <span className="text-lg sm:text-xl font-bold text-purple-600">₹{costs.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Info Card */}
            <div className="bg-blue-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-blue-200">
              <div className="flex items-start gap-2 sm:gap-3">
                <i className="ri-information-line text-blue-600 text-lg sm:text-xl mt-1"></i>
                <div>
                  <h5 className="text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">Important Note</h5>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    This is an estimated cost based on average prices. Actual costs may vary depending on the specific hospital, doctor fees, and individual medical requirements. Please contact hospitals directly for accurate quotes.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={scrollToHospitals}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-purple-600 text-white rounded-xl font-semibold text-sm sm:text-base hover:bg-purple-700 transition-all hover:shadow-lg shadow-purple-600/30 whitespace-nowrap"
            >
              <i className="ri-hospital-line mr-2"></i>
              Find Hospitals in Your Budget
            </button>
          </div>
        </div>

        {/* Cost Comparison by Category */}
        <div
          ref={comparisonRef}
          className={`mt-12 sm:mt-16 md:mt-20 bg-white rounded-2xl sm:rounded-3xl shadow-xl p-5 sm:p-6 md:p-8 ${comparisonVisible ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'}`}
        >
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">Average Cost Comparison by Category</h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="p-4 sm:p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl sm:rounded-2xl border-2 border-emerald-200">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                  <i className="ri-hand-coin-line text-lg sm:text-xl text-white"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900">Affordable</h4>
              </div>
              <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">Normal Delivery</span>
                  <span className="font-bold text-emerald-700">₹15,000</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">C-Section</span>
                  <span className="font-bold text-emerald-700">₹35,000</span>
                </div>
              </div>
              <p className="text-xs text-gray-600">Best for budget-conscious families</p>
            </div>

            <div className="p-4 sm:p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl sm:rounded-2xl border-2 border-amber-200">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-full flex items-center justify-center">
                  <i className="ri-star-line text-lg sm:text-xl text-white"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900">Mid-Range</h4>
              </div>
              <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">Normal Delivery</span>
                  <span className="font-bold text-amber-700">₹40,000</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">C-Section</span>
                  <span className="font-bold text-amber-700">₹80,000</span>
                </div>
              </div>
              <p className="text-xs text-gray-600">Balance of quality and affordability</p>
            </div>

            <div className="p-4 sm:p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl sm:rounded-2xl border-2 border-purple-200 sm:col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 rounded-full flex items-center justify-center">
                  <i className="ri-vip-crown-line text-lg sm:text-xl text-white"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900">Premium</h4>
              </div>
              <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">Normal Delivery</span>
                  <span className="font-bold text-purple-700">₹70,000</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">C-Section</span>
                  <span className="font-bold text-purple-700">₹1,50,000</span>
                </div>
              </div>
              <p className="text-xs text-gray-600">Advanced facilities and expert care</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
